import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import WebApp from '@twa-dev/sdk';

interface TelegramContextType {
  webApp: typeof WebApp;
  initData: string | null;
  user: any;
  isBrowserMode: boolean;
  /**
   * Переопределяет нативный Back в Telegram: например, шаг назад в мастере вместо history.back().
   * Передавайте null при размонтировании экрана.
   */
  setTelegramBackOverride: (handler: (() => void) | null) => void;
}

const TelegramContext = createContext<TelegramContextType | null>(null);

/** Наш собственный кэш initData: переживает потерю фрагмента при редиректах внутри WebView. */
const RAW_INIT_DATA_KEY = 'rodbt_tg_init_data';

function readHashParam(hash: string, key: string): string {
  try {
    const h = (hash || '').replace(/^#/, '');
    if (!h) return '';
    // Telegram кладёт параметры прямо во фрагмент: #tgWebAppData=...&tgWebAppVersion=...
    const qIndex = h.indexOf('?');
    const search = qIndex >= 0 ? h.slice(qIndex + 1) : h;
    return new URLSearchParams(search).get(key) || '';
  } catch {
    return '';
  }
}

/**
 * Достаёт initData из всех доступных источников по порядку надёжности.
 *
 * Зачем каскад: Telegram передаёт initData во фрагменте URL (#tgWebAppData=...), а фрагмент
 * теряется при любой полной перезагрузке/редиректе внутри мини-аппа (наш ранний скрипт версии,
 * редирект по 401, <Navigate to="/login">). После этого WebApp.initData может оказаться пустым,
 * и пользователь видел «Не удалось получить данные авторизации из Telegram».
 */
export function resolveTelegramInitData(): string {
  const pick = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : '');
  let raw = '';

  try {
    raw = pick((WebApp as any)?.initData);
  } catch {
    /* SDK может быть недоступен вне Telegram */
  }
  if (!raw) {
    try {
      raw = pick((window as any).Telegram?.WebApp?.initData);
    } catch {
      /* */
    }
  }
  // Текущий фрагмент URL
  if (!raw) raw = readHashParam(window.location.hash, 'tgWebAppData');
  // URL холодного старта: переживает history.replaceState и <Navigate>
  if (!raw) {
    try {
      const nav = performance.getEntriesByType('navigation')[0] as any;
      if (nav?.name) raw = readHashParam(new URL(nav.name).hash, 'tgWebAppData');
    } catch {
      /* */
    }
  }
  // Бутстрап самого telegram-web-app.js
  if (!raw) {
    try {
      const stored = JSON.parse(sessionStorage.getItem('__telegram__initParams') || 'null');
      raw = pick(stored?.tgWebAppData);
    } catch {
      /* */
    }
  }
  // Наш собственный кэш
  if (!raw) {
    try {
      raw = pick(sessionStorage.getItem(RAW_INIT_DATA_KEY));
    } catch {
      /* */
    }
  }

  if (raw) {
    try {
      sessionStorage.setItem(RAW_INIT_DATA_KEY, raw);
    } catch {
      /* приватный режим / заблокированное хранилище */
    }
  }
  return raw;
}

function hasTelegramWebApp(): boolean {
  try {
    return typeof window !== 'undefined' && (window as any).Telegram?.WebApp !== undefined;
  } catch {
    return false;
  }
}

export const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Значения вычисляются СИНХРОННО до первого рендера.
  // Раньше они ставились в useLayoutEffect, и useEffect в LoginPage успевал отработать
  // со старым замыканием (initData=null, isBrowserMode=false) — из-за чего показывался
  // попап «Не удалось получить данные авторизации из Telegram» даже при живом initData.
  const bootRef = useRef<{ initData: string | null; isBrowserMode: boolean } | null>(null);
  if (!bootRef.current) {
    const raw = resolveTelegramInitData();
    bootRef.current = { initData: raw || null, isBrowserMode: !raw };
  }

  const [initData, setInitData] = useState<string | null>(bootRef.current.initData);
  const [user, setUser] = useState<any>(null);
  const [isBrowserMode, setIsBrowserMode] = useState<boolean>(bootRef.current.isBrowserMode);
  const backButtonOverrideRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.has('_tgrel')) {
        u.searchParams.delete('_tgrel');
        const q = u.searchParams.toString();
        window.history.replaceState(null, '', u.pathname + (q ? '?' + q : '') + u.hash);
      }
    } catch {
      /* */
    }
  }, []);

  // Если мы внутри Telegram, но initData ещё не подъехал — не сдаёмся сразу, а перепроверяем.
  useEffect(() => {
    if (initData || !hasTelegramWebApp()) return;
    let cancelled = false;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const raw = resolveTelegramInitData();
      if (cancelled) return;
      if (raw) {
        setInitData(raw);
        setIsBrowserMode(false);
        clearInterval(timer);
      } else if (attempts >= 6) {
        clearInterval(timer);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [initData]);

  const setTelegramBackOverride = useCallback((handler: (() => void) | null) => {
    backButtonOverrideRef.current = handler;
    try {
      if (handler) {
        WebApp.BackButton.show();
      } else {
        WebApp.BackButton.hide();
      }
    } catch {
      /* вне Telegram / без BackButton */
    }
  }, []);

  useLayoutEffect(() => {
    if (!hasTelegramWebApp() || !bootRef.current?.initData) {
      if (!hasTelegramWebApp()) {
        console.log('Running in browser mode (Telegram WebApp not available)');
      }
      return;
    }

    let applyTelegramTheme: (() => void) | undefined;

    try {
      WebApp.ready();
      WebApp.expand();

      applyTelegramTheme = () => {
        const root = document.documentElement;
        const tp = WebApp.themeParams;
        if (WebApp.colorScheme === 'dark') {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
        if (tp.bg_color) {
          root.style.setProperty('--tg-bg', tp.bg_color);
        } else {
          root.style.removeProperty('--tg-bg');
        }
        if (tp.text_color) {
          root.style.setProperty('--tg-text', tp.text_color);
        } else {
          root.style.removeProperty('--tg-text');
        }
        if (tp.button_color) {
          root.style.setProperty('--tg-button', tp.button_color);
        } else {
          root.style.removeProperty('--tg-button');
        }
        if (tp.hint_color) {
          root.style.setProperty('--tg-hint', tp.hint_color);
        } else {
          root.style.removeProperty('--tg-hint');
        }
      };
      applyTelegramTheme();
      WebApp.onEvent('themeChanged', applyTelegramTheme);

      if (WebApp.initDataUnsafe?.user) {
        setUser(WebApp.initDataUnsafe.user);
      }

      WebApp.BackButton.onClick(() => {
        const override = backButtonOverrideRef.current;
        if (override) {
          override();
          return;
        }
        window.history.back();
      });
    } catch (error) {
      console.error('Error initializing Telegram WebApp:', error);
    }

    return () => {
      if (applyTelegramTheme) {
        try {
          WebApp.offEvent('themeChanged', applyTelegramTheme);
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  return (
    <TelegramContext.Provider
      value={{
        webApp: WebApp,
        initData,
        user,
        isBrowserMode,
        setTelegramBackOverride,
      }}
    >
      {children}
    </TelegramContext.Provider>
  );
};

export const useTelegram = () => {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within TelegramProvider');
  }
  return context;
};
